/**
 * NL-014 calculation explanation.
 *
 * Turns the contract chain's verdicts into something a visitor can read: which
 * method produced the estimate, which package rule fired, which seasonal
 * assumptions were applied, what the resulting range is, and what is blocking a
 * fuller answer.
 *
 * It is informational only. It authorizes nothing, is never persisted, never
 * leaves the session, and is not evidence of a price, a quote, or a commitment.
 *
 * ## Privacy boundary
 *
 * The explanation is built by *construction* rather than by filtering: every
 * entry carries a stable `id` (which selects localized copy) plus a small map of
 * values drawn only from an allowlist of safe primitives - version strings,
 * enum members, integers, and provenance hashes. No planner free text
 * (`goal`, `icp`, objections, notes, campaign names), no contact detail, no
 * product name, no raw money amount, and no secret can reach it, because no code
 * path copies those fields in. `REDACTED_STATE_FIELDS` names the planner inputs
 * this module must never read, and `calculation-explanation.test.ts` asserts the
 * rendered output contains none of them.
 *
 * Pure: no clock, no I/O, no state mutation.
 */
import { DECIMAL_ROUNDING_CONTRACT_VERSION } from "@/lib/calculator-contracts/decimal";
import {
  FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION,
  type FunnelCalculationReceipt,
} from "@/lib/funnel-planner/calculation-receipt";
import type { PlannerPackageAdvice } from "@/lib/funnel-planner/package-adviser";
import type { ManualSeasonalScenarioResult } from "@/lib/funnel-planner/seasonal-scenarios";
import type { FunnelStateVersion } from "@/lib/funnel-planner/schema";

export const CALCULATION_EXPLANATION_VERSION = "nl014-funnel-calculation-explanation-v1";

/**
 * Planner state fields this module is forbidden from reading. Named here so the
 * privacy test can assert their absence rather than trusting review.
 */
export const REDACTED_STATE_FIELDS = [
  "goal",
  "icp",
  "icpSegment",
  "buyingTrigger",
  "primaryObjection",
  "mainObjection",
  "decisionMaker",
  "contact",
  "email",
  "name",
  "company",
  "campaignName",
  "note",
  "notes",
] as const;

export type CalculationExplanationSectionId =
  | "method"
  | "package"
  | "seasonal"
  | "range"
  | "blockers";

/** Values are restricted to types that cannot carry free-form visitor input. */
export type CalculationExplanationValue = string | number | boolean;

/**
 * Every id this module can emit.
 *
 * Closed on purpose: `lib/funnel-planner/explanation-copy.ts` maps each id to a
 * localized copy key, and an exhaustive map means a new entry cannot ship
 * rendering its raw machine values to a visitor.
 */
export const CALCULATION_EXPLANATION_ENTRY_IDS = [
  "explanation.method.classification",
  "explanation.method.receipt",
  "explanation.method.component",
  "explanation.package.rule",
  "explanation.package.reason",
  "explanation.package.band",
  "explanation.package.pricingBlocked",
  "explanation.package.missing",
  "explanation.seasonal.method",
  "explanation.seasonal.scenario",
  "explanation.seasonal.blocked",
  "explanation.range.scenario",
  "explanation.blocker",
] as const;

export type CalculationExplanationEntryId = (typeof CALCULATION_EXPLANATION_ENTRY_IDS)[number];

export type CalculationExplanationEntry = {
  /** Stable key selecting localized copy; never a rendered English sentence. */
  readonly id: CalculationExplanationEntryId;
  readonly section: CalculationExplanationSectionId;
  readonly values?: Readonly<Record<string, CalculationExplanationValue>>;
};

export type CalculationExplanation = {
  readonly explanationVersion: typeof CALCULATION_EXPLANATION_VERSION;
  /** Structurally pinned; there is no representation for a quote here. */
  readonly classification: typeof FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION;
  readonly funnelStateVersion: FunnelStateVersion;
  readonly entries: readonly CalculationExplanationEntry[];
  readonly blockers: readonly string[];
  readonly hasReceipt: boolean;
};

export type CalculationExplanationInput = {
  readonly funnelStateVersion: FunnelStateVersion;
  readonly packageAdvice: PlannerPackageAdvice | null;
  readonly seasonal: ManualSeasonalScenarioResult | null;
  readonly receipt: FunnelCalculationReceipt | null;
  /** Blockers raised by the NL-014 builder when it refused to emit a receipt. */
  readonly receiptBlockers?: readonly string[];
};

function methodEntries(input: CalculationExplanationInput): CalculationExplanationEntry[] {
  const entries: CalculationExplanationEntry[] = [
    {
      id: "explanation.method.classification",
      section: "method",
      values: {
        classification: FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION,
        explanationVersion: CALCULATION_EXPLANATION_VERSION,
        funnelStateVersion: input.funnelStateVersion,
        // Always reported, so the rounding rule is visible even when no full
        // NL-014 receipt was built for this session.
        decimalRoundingContractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
      },
    },
  ];

  if (input.receipt) {
    entries.push({
      id: "explanation.method.receipt",
      section: "method",
      values: {
        decimalRoundingContractVersion: input.receipt.decimalRoundingContractVersion,
        outputScale: input.receipt.outputScale,
        calculationTimestamp: input.receipt.calculationTimestamp,
        inputsHash: input.receipt.inputsHash,
        outputsHash: input.receipt.outputsHash,
      },
    });

    for (const [component, reference] of Object.entries(input.receipt.components)) {
      entries.push({
        id: "explanation.method.component",
        section: "method",
        values: {
          component,
          status: reference.status,
          contractVersion: reference.contractVersion ?? "",
        },
      });
    }
  }

  return entries;
}

function packageEntries(advice: PlannerPackageAdvice | null): CalculationExplanationEntry[] {
  if (!advice) return [];

  const entries: CalculationExplanationEntry[] = [
    {
      id: "explanation.package.rule",
      section: "package",
      values: {
        ruleVersion: advice.ruleVersion,
        recommendedTier: advice.recommendedTier,
        decision: advice.decision,
        selectedTier: advice.selectedTier ?? "",
        manualQuoteRequired: advice.manualQuoteRequired,
      },
    },
  ];

  for (const reason of advice.reasons) {
    entries.push({ id: "explanation.package.reason", section: "package", values: { reason } });
  }

  if (advice.pricingBand && advice.pricingSnapshotVersion) {
    entries.push({
      id: "explanation.package.band",
      section: "package",
      values: {
        pricingBand: advice.pricingBand,
        pricingSnapshotVersion: advice.pricingSnapshotVersion,
        pricingCurrency: advice.pricingCurrency ?? "",
      },
    });
  }

  for (const blocker of advice.pricingContextBlockers) {
    entries.push({ id: "explanation.package.pricingBlocked", section: "package", values: { blocker } });
  }
  for (const missing of advice.missingInformation) {
    entries.push({ id: "explanation.package.missing", section: "package", values: { missing } });
  }

  return entries;
}

function seasonalEntries(seasonal: ManualSeasonalScenarioResult | null): CalculationExplanationEntry[] {
  if (!seasonal) return [];

  const entries: CalculationExplanationEntry[] = [
    {
      id: "explanation.seasonal.method",
      section: "seasonal",
      values: {
        methodVersion: seasonal.methodVersion,
        readiness: seasonal.readiness,
        appliedTo: seasonal.appliedTo,
        revenueMultiplierApplied: seasonal.revenueMultiplierApplied,
        assumptionLabel: seasonal.assumptionLabel ?? "",
      },
    },
  ];

  for (const scenario of seasonal.scenarios) {
    entries.push({
      id: "explanation.seasonal.scenario",
      section: "seasonal",
      values: {
        scenario: scenario.scenario,
        percent: scenario.percent,
        multiplier: scenario.multiplier,
      },
    });
  }

  for (const blocker of seasonal.blockers) {
    entries.push({ id: "explanation.seasonal.blocked", section: "seasonal", values: { blocker } });
  }

  return entries;
}

function rangeEntries(seasonal: ManualSeasonalScenarioResult | null): CalculationExplanationEntry[] {
  if (!seasonal || seasonal.readiness !== "ready") return [];

  return seasonal.scenarios.map((scenario) => ({
    id: "explanation.range.scenario",
    section: "range" as const,
    values: {
      scenario: scenario.scenario,
      monthlyVisitors: scenario.monthlyVisitors,
    },
  }));
}

/**
 * Assembles the explanation.
 *
 * Blockers are collected across all sources rather than short circuiting, so a
 * visitor sees everything limiting the estimate in one place.
 */
export function buildCalculationExplanation(
  input: CalculationExplanationInput,
): CalculationExplanation {
  const blockers: string[] = [
    ...(input.receiptBlockers ?? []),
    ...(input.seasonal?.blockers ?? []),
    ...(input.packageAdvice?.pricingContextBlockers ?? []),
  ];

  const entries: CalculationExplanationEntry[] = [
    ...methodEntries(input),
    ...packageEntries(input.packageAdvice),
    ...seasonalEntries(input.seasonal),
    ...rangeEntries(input.seasonal),
    ...blockers.map((blocker): CalculationExplanationEntry => ({
      id: "explanation.blocker",
      section: "blockers",
      values: { blocker },
    })),
  ];

  return {
    explanationVersion: CALCULATION_EXPLANATION_VERSION,
    classification: FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION,
    funnelStateVersion: input.funnelStateVersion,
    entries,
    blockers: [...new Set(blockers)],
    hasReceipt: input.receipt !== null,
  };
}

/**
 * Flattens the explanation to the exact strings it would render, so a privacy
 * test can assert that no redacted planner input appears anywhere in it.
 */
export function flattenExplanationValues(explanation: CalculationExplanation): readonly string[] {
  return explanation.entries.flatMap((entry) => [
    entry.id,
    ...Object.entries(entry.values ?? {}).flatMap(([key, value]) => [key, String(value)]),
  ]);
}
